import React, { useState } from "react";
import Column from "@components/column";
import {
  Phone,
  Mail,
  Calendar,
  ShieldCheck,
  MessageCircle,
  Share2,
  CheckCircle,
  Copy,
  Bell,
  Wallet,
} from "lucide-react";
import { useModal } from "@context/ModalContext";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import * as S from "./dashboardStyle";
import Typography from "@components/typography";
import { useNavigate } from "react-router-dom";
import { Helper } from "@utils/index";
import useConfig from "@hooks/useConfig";
import CustomerOverviewDashboard from "./CustomerOverviewDashboard";
import RecentNotifications from "@components/recent-notifications";
import PushNotificationForm from "@commons/push-notifications/form";

const Overview = ({ state, customerId }: { state: any; customerId: number }) => {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { settings: currencySettings } = useGeneralSettings();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { bucketUrl } = useConfig();
  if (!state) return <Typography>No data available</Typography>;

  const {
    name,
    email,
    phoneNo,
    avatar,
    isActive,
    createdAt,
  } = state;

  const baseURL = bucketUrl + "/";

  // Copy to clipboard function
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const ContactCustomerModal = () => {
    const contactMethods = [
      {
        icon: Phone,
        label: "Phone Number",
        value: phoneNo || "N/A",
        type: "phone",
        action: phoneNo ? `tel:${phoneNo}` : undefined,
      },
      {
        icon: Mail,
        label: "Email Address",
        value: email || "N/A",
        type: "email",
        action: email ? `mailto:${email}` : undefined,
      },
    ];

    return (
      <div style={{ padding: "24px", background: "white", borderRadius: "16px" }}>
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px", alignItems: "center" }}>
          <S.AvatarContainer style={{ width: 64, height: 64, border: "2px solid #f1f5f9", boxShadow: "none" }}>
            {avatar ? (
              <S.AvatarImage src={baseURL + avatar} alt={name} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: Helper.getDynamicColor(name || "Customer"), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: "bold", fontSize: 24 }}>
                {Helper.getInitials(name || "Customer")}
              </div>
            )}
          </S.AvatarContainer>
          <div>
            <Typography weight={700} size={20}>Contact {name}</Typography>
            <Typography size={14} color="#64748b">Get in touch with the customer</Typography>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {contactMethods.map((method, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
              <div style={{ width: 40, height: 40, borderRadius: "10px", background: "#fff7ed", color: "#ff8c00", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <method.icon size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <Typography size={12} color="#64748b" weight={600} styleText="text-transform: uppercase;">{method.label}</Typography>
                <Typography size={14} weight={600} color="#1e293b">{method.value}</Typography>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {method.action && (
                  <a href={method.action} style={{ width: 36, height: 36, borderRadius: "8px", background: "white", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
                    {method.type === 'phone' ? <Phone size={16} /> : <Mail size={16} />}
                  </a>
                )}
                <button 
                  onClick={() => copyToClipboard(method.value, method.label)}
                  style={{ width: 36, height: 36, borderRadius: "8px", background: copiedField === method.label ? "#10b981" : "white", border: copiedField === method.label ? "1px solid #10b981" : "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: copiedField === method.label ? "white" : "#64748b", cursor: "pointer", transition: "all 0.2s" }}
                >
                  {copiedField === method.label ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleContactCustomer = () => {
    showModal(<ContactCustomerModal />, { width: "500px" });
  };

  const handleLoadWallet = () => {
    showModal(
      <WalletAdjustmentModal
        user={state}
        onClose={hideModal}
        onSuccess={() => {
          window.location.reload();
        }}
        currencySettings={currencySettings}
      />,
    );
  };

  return (
    <S.Container>
      {/* Customer Header Section */}
      <S.StoreHeaderSection>
        <S.CoverImageContainer>
          <S.CoverImage
             style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)" }}
          />
          <S.HeaderOverlay />

          <S.StoreHeaderContent>
            <S.AvatarContainer>
              {avatar ? (
                <S.AvatarImage src={baseURL + avatar} alt="Customer avatar" />
              ) : (
                <div style={{ width: 140, height: 140, borderRadius: "50%", background: Helper.getDynamicColor(name || "Customer"), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: "bold", fontSize: 48, boxShadow: "inset 0 0 20px rgba(0,0,0,0.1)" }}>
                  {Helper.getInitials(name || "Customer")}
                </div>
              )}
              {isActive && <S.OnlineIndicator />}
            </S.AvatarContainer>
            <S.StoreInfo>
              <S.StoreNameRow>
                <Typography weight={700} size={28} color="white">
                  {name}
                </Typography>
                <S.BadgeContainer>
                  {isActive ? (
                    <S.VerificationBadge style={{ background: "#10b981" }}>
                      <CheckCircle size={14} /> Active
                    </S.VerificationBadge>
                  ) : (
                    <S.VerificationBadge style={{ background: "#ef4444" }}>
                      Inactive
                    </S.VerificationBadge>
                  )}
                </S.BadgeContainer>
              </S.StoreNameRow>

              <S.StoreMeta>
                <S.MetaItem>
                  <Phone size={16} color="white" />
                  <Typography size={14} color="white">
                    {phoneNo || "N/A"}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <Mail size={16} color="white" />
                  <Typography size={14} color="white">
                    {email || "N/A"}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <Calendar size={16} color="white" />
                  <Typography size={14} color="white">
                    Joined {new Date(createdAt).toLocaleDateString()}
                  </Typography>
                </S.MetaItem>
              </S.StoreMeta>

              <S.ActionButtons>
                <S.ActionButton primary onClick={handleContactCustomer}>
                  <MessageCircle size={16} />
                  Contact Customer
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    // Navigate to edit user - currently we'll just redirect to the lists view as there might not be a dedicated update-customer route
                    navigate(`/admin/users/customer/customer-lists`);
                  }}
                >
                  <Share2 size={16} />
                  Go to Lists
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    showModal(
                      <PushNotificationForm
                        initialData={{
                          targetAudience: "specific_customer",
                          targetId: state.userId || state.id,
                          title: `Customer Notification: ${name}`,
                        }}
                        onSuccess={() => hideModal()}
                      />,
                      { width: "1000px" },
                    );
                  }}
                >
                  <Bell size={16} />
                  Send Push
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    navigate(
                      `/admin/chat?userId=${state.userId || state.id}&role=customer`,
                    );
                  }}
                >
                  <MessageCircle size={16} />
                  Chat
                </S.ActionButton>
                <S.ActionButton onClick={handleLoadWallet}>
                  <Wallet size={16} />
                  Load Wallet
                </S.ActionButton>
              </S.ActionButtons>
            </S.StoreInfo>
          </S.StoreHeaderContent>
        </S.CoverImageContainer>
      </S.StoreHeaderSection>

      <S.ContentGrid>
        <S.LeftColumn>
           {/* Customer Overview Dashboard */}
           <CustomerOverviewDashboard customerId={customerId} />
        </S.LeftColumn>

        <S.RightColumn>
          {/* Recent Notifications */}
          <S.Section>
            <RecentNotifications userId={customerId} role="customer" />
          </S.Section>
        </S.RightColumn>
      </S.ContentGrid>

    </S.Container>
  );
};

export default Overview;
